/**
 * SEO 配置 - character-detail（角色详情页）
 *
 * 角色名和描述来自 API（随语言变化），此文件只提供 title 后缀、description 模板和 keywords 后缀。
 * 页面中通过 t('seo.characterDetail.titleSuffix') 等方式使用。
 *
 * ⚠️ 注意：vue-i18n 中 "|" 是复数分隔符，不能直接出现在翻译值中！
 * 因此所有 key 不含 "| WeryAI"，在 JS 代码中拼接。
 *
 * 标题格式：{titleFallback} - {角色名} | WeryAI（≤60 字符）
 * 超长退化：{titleSuffix} - {角色名} | WeryAI
 * 描述格式：descTemplate 整句模板（含 {name}/{gender}/{tags}/{intro} 占位符）→ 100-160 字符
 *
 * key 清单（5 个）：
 *  - titleSuffix:    title 超长退化时使用的短版前缀，如 "AI Character"
 *  - titleFallback:  title 的首选前缀（关键词前置），如 "Free AI Character Chat"
 *  - descTemplate:   description 整句模板（包含 {name}/{gender}/{tags}/{intro} 占位符）
 *  - descFallback:   关键字段全空时的兜底 description（包含 {name} 占位符）
 *  - keywordSuffix:  keywords 末尾追加的通用关键词尾缀
 */

export default {
    en: {
        characterDetail: {
            titleSuffix: 'AI Character',
            titleFallback: 'Free AI Character Chat',
            descTemplate: 'Chat with {name} for free on WeryAI — a {gender} AI character tagged {tags}. {intro} Start your free AI character chat now!',
            descFallback: 'Discover {name} on WeryAI — explore this AI character\'s profile, personality, and tags, then start a free chat conversation.',
            keywordSuffix: 'free AI character chat',
        },
    },
    zh: {
        characterDetail: {
            titleSuffix: 'AI 角色',
            titleFallback: '免费AI角色聊天',
            descTemplate: '在 WeryAI 免费与 {name} 聊天——{gender} AI 角色，标签：{tags}。{intro} 立即开始免费AI角色聊天！',
            descFallback: '在 WeryAI 上发现 {name}——探索该 AI 角色的资料、性格与标签，立即开启免费聊天。',
            keywordSuffix: '免费AI角色聊天',
        },
    },
    ja: {
        characterDetail: {
            titleSuffix: 'AIキャラ',
            titleFallback: '無料AIキャラチャット',
            descTemplate: 'WeryAI で {name} と無料チャット——{gender} AIキャラ、タグ：{tags}。{intro} 今すぐ無料AIキャラチャットを始めましょう！',
            descFallback: 'WeryAI で {name} を発見——この AI キャラクターのプロフィール・性格・タグを探索し、無料チャットを開始しましょう。',
            keywordSuffix: '無料AIキャラチャット',
        },
    },
    ko: {
        characterDetail: {
            titleSuffix: 'AI 캐릭터',
            titleFallback: '무료 AI 캐릭터 채팅',
            descTemplate: 'WeryAI에서 {name}와 무료 채팅 — {gender} AI 캐릭터, 태그: {tags}. {intro} 지금 무료 AI 캐릭터 채팅을 시작하세요!',
            descFallback: 'WeryAI에서 {name}을(를) 발견하세요 — 이 AI 캐릭터의 프로필, 성격, 태그를 탐색한 뒤 무료 채팅을 시작하세요.',
            keywordSuffix: '무료 AI 캐릭터 채팅',
        },
    },
    tw: {
        characterDetail: {
            titleSuffix: 'AI 角色',
            titleFallback: '免費AI角色聊天',
            descTemplate: '在 WeryAI 免費與 {name} 聊天——{gender} AI 角色，標籤：{tags}。{intro} 立即開始免費AI角色聊天！',
            descFallback: '在 WeryAI 上發現 {name}——探索該 AI 角色的資料、性格與標籤，立即開啟免費聊天。',
            keywordSuffix: '免費AI角色聊天',
        },
    },
    es: {
        characterDetail: {
            titleSuffix: 'Personaje IA',
            titleFallback: 'Chat Gratis con Personaje IA',
            descTemplate: 'Chatea gratis con {name} en WeryAI — un personaje IA {gender} con etiquetas {tags}. {intro} ¡Empieza tu chat gratis con personaje IA ahora!',
            descFallback: 'Descubre a {name} en WeryAI — explora el perfil, la personalidad y las etiquetas de este personaje IA, y empieza un chat gratis.',
            keywordSuffix: 'chat gratis de personaje IA',
        },
    },
    fr: {
        characterDetail: {
            titleSuffix: 'Personnage IA',
            titleFallback: 'Chat Gratuit Personnage IA',
            descTemplate: 'Discutez gratuitement avec {name} sur WeryAI — un personnage IA {gender} avec les tags {tags}. {intro} Commencez votre chat gratuit maintenant !',
            descFallback: 'Découvrez {name} sur WeryAI — explorez le profil, la personnalité et les tags de ce personnage IA, puis démarrez un chat gratuit.',
            keywordSuffix: 'chat gratuit personnage IA',
        },
    },
    de: {
        characterDetail: {
            titleSuffix: 'KI-Charakter',
            titleFallback: 'Kostenloser KI-Charakter-Chat',
            descTemplate: 'Chatten Sie kostenlos mit {name} auf WeryAI — ein {gender} KI-Charakter mit den Tags {tags}. {intro} Starten Sie jetzt Ihren kostenlosen KI-Chat!',
            descFallback: 'Entdecken Sie {name} auf WeryAI — erkunden Sie das Profil, die Persönlichkeit und die Tags dieses KI-Charakters und starten Sie einen kostenlosen Chat.',
            keywordSuffix: 'kostenloser KI-Charakter-Chat',
        },
    },
    ru: {
        characterDetail: {
            titleSuffix: 'ИИ-персонаж',
            titleFallback: 'Бесплатный чат с ИИ',
            descTemplate: 'Общайтесь бесплатно с {name} на WeryAI — {gender} ИИ-персонаж, теги: {tags}. {intro} Начните бесплатный чат с ИИ-персонажем прямо сейчас!',
            descFallback: 'Откройте для себя {name} на WeryAI — исследуйте профиль, характер и теги этого ИИ-персонажа, а затем начните бесплатный чат.',
            keywordSuffix: 'бесплатный чат с ИИ-персонажем',
        },
    },
    pl: {
        characterDetail: {
            titleSuffix: 'Postać AI',
            titleFallback: 'Darmowy Czat z Postacią AI',
            descTemplate: 'Rozmawiaj za darmo z {name} na WeryAI — {gender} postać AI, tagi: {tags}. {intro} Rozpocznij darmowy czat z postacią AI teraz!',
            descFallback: 'Odkryj {name} na WeryAI — poznaj profil, osobowość i tagi tej postaci AI, a następnie rozpocznij darmowy czat.',
            keywordSuffix: 'darmowy czat z postacią AI',
        },
    },
    pt: {
        characterDetail: {
            titleSuffix: 'Personagem IA',
            titleFallback: 'Chat Grátis com Personagem IA',
            descTemplate: 'Converse grátis com {name} no WeryAI — um personagem IA {gender} com tags {tags}. {intro} Comece seu chat grátis com personagem IA agora!',
            descFallback: 'Descubra {name} no WeryAI — explore o perfil, a personalidade e as tags deste personagem IA e inicie um chat grátis.',
            keywordSuffix: 'chat grátis com personagem IA',
        },
    },
    it: {
        characterDetail: {
            titleSuffix: 'Personaggio IA',
            titleFallback: 'Chat Gratis Personaggio IA',
            descTemplate: 'Chatta gratis con {name} su WeryAI — un personaggio IA {gender} con i tag {tags}. {intro} Inizia la tua chat gratuita con personaggio IA adesso!',
            descFallback: 'Scopri {name} su WeryAI — esplora il profilo, la personalità e i tag di questo personaggio IA, poi avvia una chat gratuita.',
            keywordSuffix: 'chat gratis personaggio IA',
        },
    },
    ar: {
        characterDetail: {
            titleSuffix: 'شخصية AI',
            titleFallback: 'دردشة مجانية مع شخصية AI',
            descTemplate: 'دردش مجانًا مع {name} على WeryAI — شخصية AI {gender} بوسوم {tags}. {intro} ابدأ دردشتك المجانية مع شخصية AI الآن!',
            descFallback: 'اكتشف {name} على WeryAI — استكشف الملف الشخصي والشخصية والوسوم لهذه الشخصية الذكية، ثم ابدأ محادثة مجانية.',
            keywordSuffix: 'دردشة مجانية شخصية AI',
        },
    },
    th: {
        characterDetail: {
            titleSuffix: 'ตัวละคร AI',
            titleFallback: 'แชทฟรีกับตัวละคร AI',
            descTemplate: 'แชทฟรีกับ {name} บน WeryAI — ตัวละคร AI {gender} แท็ก {tags} {intro} เริ่มแชทฟรีกับตัวละคร AI เลย!',
            descFallback: 'ค้นพบ {name} บน WeryAI — สำรวจโปรไฟล์ บุคลิกภาพ และแท็กของตัวละคร AI นี้ แล้วเริ่มแชทฟรี',
            keywordSuffix: 'แชทฟรีตัวละคร AI',
        },
    },
    da: {
        characterDetail: {
            titleSuffix: 'AI-karakter',
            titleFallback: 'Gratis AI-karakter Chat',
            descTemplate: 'Chat gratis med {name} på WeryAI — en {gender} AI-karakter med tags {tags}. {intro} Start din gratis AI-karakter chat nu!',
            descFallback: 'Opdag {name} på WeryAI — udforsk denne AI-karakters profil, personlighed og tags, og start en gratis chat.',
            keywordSuffix: 'gratis AI-karakter chat',
        },
    },
    no: {
        characterDetail: {
            titleSuffix: 'AI-karakter',
            titleFallback: 'Gratis AI-karakter Chat',
            descTemplate: 'Chat gratis med {name} på WeryAI — en {gender} AI-karakter med tagger {tags}. {intro} Start din gratis AI-karakter chat nå!',
            descFallback: 'Oppdag {name} på WeryAI — utforsk denne AI-karakterens profil, personlighet og tagger, og start en gratis chat.',
            keywordSuffix: 'gratis AI-karakter chat',
        },
    },
    nl: {
        characterDetail: {
            titleSuffix: 'AI-personage',
            titleFallback: 'Gratis AI-personage Chat',
            descTemplate: 'Chat gratis met {name} op WeryAI — een {gender} AI-personage met tags {tags}. {intro} Start nu je gratis AI-personage chat!',
            descFallback: 'Ontdek {name} op WeryAI — verken het profiel, de persoonlijkheid en de tags van dit AI-personage en start een gratis chat.',
            keywordSuffix: 'gratis AI-personage chat',
        },
    },
    id: {
        characterDetail: {
            titleSuffix: 'Karakter AI',
            titleFallback: 'Obrolan Gratis Karakter AI',
            descTemplate: 'Ngobrol gratis dengan {name} di WeryAI — karakter AI {gender} bertag {tags}. {intro} Mulai obrolan gratis karakter AI sekarang!',
            descFallback: 'Temukan {name} di WeryAI — jelajahi profil, kepribadian, dan tag karakter AI ini, lalu mulai obrolan gratis.',
            keywordSuffix: 'obrolan gratis karakter AI',
        },
    },
    tr: {
        characterDetail: {
            titleSuffix: 'AI Karakter',
            titleFallback: 'Ücretsiz AI Karakter Sohbeti',
            descTemplate: '{name} ile WeryAI\'de ücretsiz sohbet edin — {gender} AI karakter, etiketler: {tags}. {intro} Şimdi ücretsiz AI karakter sohbetine başlayın!',
            descFallback: 'WeryAI\'de {name} keşfedin — bu AI karakterin profilini, kişiliğini ve etiketlerini keşfedip ücretsiz sohbet başlatın.',
            keywordSuffix: 'ücretsiz AI karakter sohbeti',
        },
    },
}
